import { Component, inject, signal, OnInit, DestroyRef, ChangeDetectionStrategy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PromptBotService } from '../../../../../../core/services/prompt-bot.service';
import { Product, Feature, FeatureOption, FeatureOptionCategory, PromptVersion } from '../../../../../../core/models/prompt-bot.model';
import { ButtonComponent } from '../../../../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../../../../shared/components/ui/dropdown/dropdown.component';
import { TextareaComponent } from '../../../../../../shared/components/ui/textarea-input/textarea.component';
import { IconComponent } from '../../../../../../shared/icons/icon.component';

@Component({
    selector: 'app-prompt-editor-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonComponent, DropdownComponent, TextareaComponent, IconComponent],
    templateUrl: './prompt-editor-modal.component.html',
    styleUrls: ['./prompt-editor-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromptEditorModalComponent implements OnInit {
    private dialogData = inject(MAT_DIALOG_DATA);
    dialogRef = inject(MatDialogRef);
    private botService = inject(PromptBotService);
    private destroyRef = inject(DestroyRef);

    isEditMode = signal(false);
    id = signal(0);
    productId = signal(0);
    featureId = signal(0);
    optionId = signal(0);
    categoryId = signal(0);
    version = signal(0);
    promptText = signal('');


    products = signal<Product[]>([]);
    features = signal<Feature[]>([]);
    options = signal<FeatureOption[]>([]);

    productOptions = computed<DropdownOption[]>(() =>
        this.products().map(p => ({ value: String(p.id), label: p.name }))
    );

    featureOptions = computed<DropdownOption[]>(() =>
        this.features().map(f => ({ value: String(f.id), label: f.name }))
    );

    optionOptions = computed<DropdownOption[]>(() =>
        this.options().map(o => ({ value: String(o.id), label: o.name }))
    );

    // The currently selected feature option's available categories (may be empty)
    selectedOptionCategories = computed<FeatureOptionCategory[]>(() => {
        const opt = this.options().find(o => o.id === this.optionId());
        return opt?.categories?.filter(c => c.is_active !== false) ?? [];
    });

    hasCategories = computed(() => this.selectedOptionCategories().length > 0);

    categoryOptions = computed<DropdownOption[]>(() =>
        this.selectedOptionCategories().map(c => ({ value: String(c.id), label: c.name }))
    );

    canDeploy = computed(() =>
        this.productId() > 0 &&
        this.featureId() > 0 &&
        this.optionId() > 0 &&
        this.promptText().trim().length > 0 &&
        (!this.hasCategories() || this.categoryId() > 0)
    );

    String = String;

    ngOnInit() {
        this.loadProducts();
        this.loadFeatures();

        if (this.dialogData?.prompt) {
            const p = this.dialogData.prompt as PromptVersion;

            if (p.id) {
                this.isEditMode.set(true);
                this.id.set(p.id);
            }

            this.productId.set(p.product_id || 0);
            this.featureId.set(p.feature_id || 0);
            this.optionId.set(p.feature_option_id || 0);
            this.categoryId.set(p.category_id || 0);
            this.version.set(p.version || 0);
            this.promptText.set(p.prompt_text || '');


            if (p.feature_id) {
                this.fetchOptionsForFeature(p.feature_id);
            }
        }
    }

    loadProducts() {
        this.botService.getProducts()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => this.products.set(data)
            });
    }

    loadFeatures() {
        this.botService.getFeatures()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => this.features.set(data)
            });
    }

    onProductChange(id: any) {
        this.productId.set(Number(Array.isArray(id) ? id[0] : id));
    }

    onFeatureChange(id: any) {
        const fid = Number(Array.isArray(id) ? id[0] : id);
        this.featureId.set(fid);
        this.optionId.set(0);
        this.categoryId.set(0);
        this.fetchOptionsForFeature(fid);
    }

    fetchOptionsForFeature(fid: number) {
        if (fid) {
            this.botService.getFeatureOptions(fid)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (data) => this.options.set(data)
                });
        } else {
            this.options.set([]);
        }
    }

    onOptionChange(id: any) {
        const oid = Number(Array.isArray(id) ? id[0] : id);
        this.optionId.set(oid);
        // Reset category whenever the option changes; it must be re-picked
        // for the new option (only if the new option has categories).
        this.categoryId.set(0);
    }

    onCategoryChange(id: any) {
        this.categoryId.set(Number(Array.isArray(id) ? id[0] : id));
    }

    submit() {
        if (!this.canDeploy()) return;
        this.dialogRef.close({
            id: this.id(),
            product_id: this.productId(),
            feature_id: this.featureId(),
            feature_option_id: this.optionId(),
            category_id: this.hasCategories() ? this.categoryId() : null,
            version: this.version(),
            prompt_text: this.promptText()
        });

    }
}
